import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {HttpClient} from '@angular/common/http';

@Component({
  selector: 'ui-payment-table',
  templateUrl: './payment-table.component.html',
  styleUrl: './payment-table.component.scss'
})
export class PaymentTableComponent implements OnInit {

  @Output() planSelect = new EventEmitter<Plan>();
  @Input() currentPlanLookupKey?: string;
  @Input() preselectedBilling?: 'monthly' | 'yearly';
  @Input() preselectedPlanKey?: 'basic' | 'professional';

  billingInterval: 'monthly' | 'yearly' = 'yearly';
  plans: Plan[] = [];
  allPlans: Plan[] = [];
  preselectedPlan?: Plan;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.http.get<PlanFeaturesConfig>('/assets/config/plan-features.json')
      .subscribe(config => {
        this.allPlans = [];

        const planKeys = Object.keys(config.plans);

        for (const key of planKeys) {
          const meta = config.plans[key];

          if (key === 'FREE') {
            this.allPlans.push({
              name: meta.name,
              description: meta.description,
              priceLabel: '0 €',
              features: meta.features,
              priceId: meta.lookupKey!,
              popular: false,
              billingInterval: 'month',
              kicker: meta.kicker,
              ctaLabel: meta.ctaLabel,
              detail: meta.detail,
              requiresCheckout: false,
              startsTrial: false,
            });
          } else {
            // Monatlicher Tarif
            this.allPlans.push({
              name: meta.name,
              description: meta.description,
              priceLabel: meta.priceMonthly!,
              alternativePrice: meta.priceYearly,
              alternativeInterval: 'year',
              yearlySavings: meta.yearlySavings,
              features: meta.features,
              priceId: meta.lookupKeyMonthly!,
              popular: meta.popular === true,
              billingInterval: 'month',
              kicker: meta.kicker,
              ctaLabel: meta.ctaLabel,
              detail: meta.detail,
              // DOCU-1041: Basic und Pro gehen nicht mehr direkt in den Stripe-Checkout,
              // sondern starten eine 30-tägige Testphase. requiresCheckout steuert nur noch,
              // ob nach dem Absenden zu Stripe weitergeleitet wird — beim Trial nein.
              requiresCheckout: false,
              startsTrial: true,
            });

            // Jährlicher Tarif
            this.allPlans.push({
              name: meta.name,
              description: meta.description,
              priceLabel: meta.priceYearly!,
              alternativePrice: meta.priceMonthly,
              alternativeInterval: 'month',
              yearlySavings: meta.yearlySavings,
              features: meta.features,
              priceId: meta.lookupKeyYearly!,
              popular: meta.popular === true,
              billingInterval: 'year',
              kicker: meta.kicker,
              ctaLabel: meta.ctaLabel,
              detail: meta.detail,
              requiresCheckout: false,   // wie oben: Trial statt Checkout
              startsTrial: true,
            });
          }
        }

        this.allPlans.sort((a, b) => {
          const orderDiff = PLAN_ORDER[this.getBasePlanKey(a.priceId)] - PLAN_ORDER[this.getBasePlanKey(b.priceId)];
          if (orderDiff !== 0) {
            return orderDiff;
          }
          return a.billingInterval === 'month' ? -1 : 1;
        });

        if (this.preselectedBilling) {
          this.billingInterval = this.preselectedBilling;
        }

        this.filterPlans();

        if (this.preselectedPlanKey) {
          this.preselectedPlan = this.findPreselectedPlan(this.preselectedPlanKey);
        }
      });
  }

  private getBasePlanKey(lookupKey: string): string {
    return lookupKey.replace('_YEARLY', '');
  }

  filterPlans(): void {
    this.plans = this.allPlans.filter(plan => {
      if (plan.priceId === 'FREE') {
        return true;
      }
      const isYearly = plan.priceId.includes('_YEARLY');
      return this.billingInterval === 'yearly' ? isYearly : !isYearly;
    });
  }

  toggleBillingInterval(): void {
    this.billingInterval = this.billingInterval === 'monthly' ? 'yearly' : 'monthly';
    this.filterPlans();
    if (this.preselectedPlanKey) {
      this.preselectedPlan = this.findPreselectedPlan(this.preselectedPlanKey);
    }
  }

  private findPreselectedPlan(planKey: 'basic' | 'professional'): Plan | undefined {
    const prefix = planKey === 'basic' ? 'BASIC' : 'PRO';
    return this.plans.find(p => this.getBasePlanKey(p.priceId) === prefix);
  }

  onSelectPlan(plan: Plan): void {
    if (this.isCurrentPlan(plan)) {
      return;
    }
    this.planSelect.emit(plan);
  }

  isCurrentPlan(plan: Plan): boolean {
    return this.currentPlanLookupKey === plan.priceId;
  }

  trackByPlan(_: number, plan: Plan): string {
    return plan.priceId;
  }
}

export interface Plan {
  name: string;
  priceLabel: string;
  alternativePrice?: string;
  alternativeInterval?: string;
  yearlySavings?: number;
  description: string;
  priceId: string;
  popular?: boolean;
  features: PlanFeature[];
  billingInterval?: string;
  kicker?: string;
  ctaLabel?: string;
  detail?: string;
  /** true, wenn nach dem Absenden zu Stripe weitergeleitet wird. Beim Trial false. */
  requiresCheckout?: boolean;
  /** DOCU-1041: true für Basic/Pro — startet eine 30-tägige Testphase ohne Zahlungsdaten. */
  startsTrial?: boolean;
}

export interface PlanFeature {
  label: string;
  detail?: string;
}

interface PlanMeta {
  // FREE
  lookupKey?: string;
  // Paid plans
  lookupKeyMonthly?: string;
  lookupKeyYearly?: string;
  priceMonthly?: string;
  priceYearly?: string;
  yearlySavings?: number;
  popular?: boolean;
  // Gemeinsam
  name: string;
  kicker: string;
  description: string;
  detail: string;
  ctaLabel: string;
  features: PlanFeature[];
}

interface PlanFeaturesConfig {
  plans: Record<string, PlanMeta>;
}

const PLAN_ORDER: { [key: string]: number } = {
  FREE: 1,
  BASIC: 2,
  PRO: 3,
  ENTERPRISE: 4,
};
